async function call(method, path, body) {
  const res = await fetch(`/api/v1${path}`, {
    method,
    headers: { Accept: "application/json", ...(body ? { "Content-Type": "application/json" } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return null;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data.error;
    err.problems = data.problems || null;
    err.version = data.version;
    // The session ended mid-visit (expired, signed out elsewhere, access removed): the app re-checks.
    if (res.status === 401) window.dispatchEvent(new Event("pot:signed-out"));
    throw err;
  }
  return data;
}

const get = path => call("GET", path);
const enc = encodeURIComponent;
const dir = slug => `/projects/${enc(slug)}/director`;

export const api = {
  tenant: () => get("/tenant"),
  projects: () => get("/projects"),
  project: slug => get(`/projects/${enc(slug)}`),
  day: slug => get(`/projects/${enc(slug)}/day`),
  dayItems: (slug, date, area) => get(`/projects/${enc(slug)}/day/items?date=${enc(date)}&area=${enc(area)}`),
  scorecard: (slug, date, item) => get(`/projects/${enc(slug)}/day/scorecard?date=${enc(date)}&item=${enc(item)}`),
  // Recall (DEC-044): a stretch of past days ranked again; reading is open, running needs a sign-in
  recall: slug => get(`/projects/${enc(slug)}/recall`),
  runRecall: (slug, body) => call("POST", `/projects/${enc(slug)}/recall`, body),
  // Grading what a recall is missing (DEC-046), for owners
  recallGrading: slug => get(`/projects/${enc(slug)}/recall/grading`),
  startRecallGrading: slug => call("POST", `/projects/${enc(slug)}/recall/grading`),
  stopRecallGrading: slug => call("DELETE", `/projects/${enc(slug)}/recall/grading`),
  // The explainer (DEC-043): a paper's synopsis at a level, asking for one, and the reader's own level
  explanation: (slug, item, level) => get(`/projects/${enc(slug)}/explain/${enc(item)}?level=${enc(level)}`),
  explain: (slug, item, level) => call("POST", `/projects/${enc(slug)}/explain/${enc(item)}`, { level }),
  explainLevel: () => get("/me/explain-level"),
  setExplainLevel: level => call("PUT", "/me/explain-level", { level }),
  requestLink: email => call("POST", "/auth/link", { email }),
  signIn: token => call("POST", "/auth/session", { token }),
  signOut: () => call("DELETE", "/auth/session"),

  // The director, for owners (DEC-035)
  director: slug => get(dir(slug)),
  saveDirector: (slug, body) => call("PUT", dir(slug), body),
  directorVersion: (slug, version) => get(`${dir(slug)}/versions/${version}`),
  previewDirector: (slug, body) => call("POST", `${dir(slug)}/preview`, body),
  patternHits: (slug, patterns, field) => call("POST", `${dir(slug)}/hits`, { patterns, ...(field ? { field } : {}) }),
  rebuild: slug => call("POST", `${dir(slug)}/rebuild`),
  rebuildStatus: slug => get(`${dir(slug)}/rebuild`),
  judge: () => get("/judge"),
  putKey: (kind, body) => call("PUT", `/judge/keys/${enc(kind)}`, body),
  removeKey: kind => call("DELETE", `/judge/keys/${enc(kind)}`),
  testKey: (kind, model) => call("POST", `/judge/keys/${enc(kind)}/test`, { model }),
  setCap: monthlyUsd => call("PUT", "/judge/cap", { monthlyUsd }),

  // The signed-in person's working memory, library and goal switches (DEC-041), and the project's
  // team shelf (DEC-042); each answers with the whole state
  mine: slug => get(`/projects/${enc(slug)}/me`),
  hold: (slug, id, snapshot, from) => call("PUT", `/projects/${enc(slug)}/me/held/${enc(id)}`, { snapshot, from }),
  release: (slug, id) => call("DELETE", `/projects/${enc(slug)}/me/held/${enc(id)}`),
  consolidate: (slug, shelf) => call("POST", `/projects/${enc(slug)}/me/consolidate`, shelf ? { shelf } : undefined),
  keep: (slug, id, snapshot) => call("PUT", `/projects/${enc(slug)}/me/library/${enc(id)}`, { snapshot }),
  forget: (slug, id) => call("DELETE", `/projects/${enc(slug)}/me/library/${enc(id)}`),
  share: (slug, id, snapshot) => call("PUT", `/projects/${enc(slug)}/me/team/${enc(id)}`, { snapshot }),
  unshare: (slug, id) => call("DELETE", `/projects/${enc(slug)}/me/team/${enc(id)}`),
  setGoal: (slug, goal, on) => call("PUT", `/projects/${enc(slug)}/me/goals/${enc(goal)}`, { on }),
  importMine: (slug, state) => call("POST", `/projects/${enc(slug)}/me/import`, state),
};
